import React, { useEffect, useMemo } from 'react';
import { Form, Select, Space, Typography } from '@duxton-web/ui';
import dayjs from 'dayjs';
import { useLocalStorage, usePrevious } from 'react-use';
import styled from 'styled-components';

import {
  CustomTimeSliceStrategy,
  ExperimentAssignmentStrategy,
  ExpFormField,
  SELECTED_EXPERIMENT_LS_KEY,
} from '../../../helper/consts';
import StepWrapper from './StepWrapper';
import { getAllDomains } from '@/common/domains';
import { RUNNING, STARTED } from '@/constants';
import DurationSelectorComponent from '@/features/experiment/components/duration';
import { Experiment } from '@/lib/PlannerDTOs';
import { useTreatments } from '@/pages/ExperimentFormV2/contexts/ExperimentFormInfoProvider';

interface DurationStepProps {
  isViewMode?: boolean;
}

const DurationStep = ({ isViewMode = false }: DurationStepProps) => {
  const [treatments] = useTreatments();
  const form = Form.useFormInstance();

  const prevTreatmentLength = usePrevious(treatments?.length);

  const currentDomainName = Form.useWatch(ExpFormField.DOMAIN, {
    form,
    preserve: true,
  });

  const currentStrategyName = Form.useWatch(ExpFormField.STRATEGY.NAME, {
    form,
    preserve: true,
  });
  const currentCustomTimeSliceStrategyName = Form.useWatch(
    ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_NAME,
    {
      form,
      preserve: true,
    },
  );

  const restrictBy = Form.useWatch(ExpFormField.TARGET.VALUE, {
    form,
    preserve: true,
  });

  const overlapByDefault = Form.useWatch(ExpFormField.OVERLAP_BY_DEFAULT, {
    form,
    preserve: true,
  });

  const geography = useMemo(() => {
    if (!currentDomainName) return null;
    const allDomains = getAllDomains();
    const domain = allDomains.find((d) => d.name === currentDomainName);
    return domain.restrictBy;
  }, [currentDomainName]);

  const strategyName = Form.useWatch(ExpFormField.STRATEGY.NAME, {
    form,
    preserve: true,
  });

  const isBalancedShuffleTimeSlice =
    currentStrategyName === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE &&
    currentCustomTimeSliceStrategyName ===
      CustomTimeSliceStrategy.BALANCED_SHUFFLE;

  const startTime = Form.useWatch(ExpFormField.START, { form, preserve: true });
  const endTime = Form.useWatch(ExpFormField.END, { form, preserve: true });

  const formattedStartTime = startTime ? dayjs(startTime) : undefined;
  const formattedEndTime = endTime ? dayjs(endTime) : undefined;

  const durationInDays = useMemo(() => {
    if (formattedStartTime && formattedEndTime) {
      return formattedEndTime.diff(formattedStartTime, 'days');
    }
    return undefined;
  }, [formattedStartTime, formattedEndTime]);

  const [selectedExperiment] = useLocalStorage<Experiment>(
    SELECTED_EXPERIMENT_LS_KEY,
  );

  useEffect(() => {
    if (
      prevTreatmentLength &&
      treatments.length &&
      prevTreatmentLength !== treatments.length
    ) {
      form.setFieldValue(
        ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_DAY_LENGTH,
        undefined,
      );

      form.setFieldValue(ExpFormField.END, undefined);
    }
  }, [prevTreatmentLength, treatments?.length]);

  const onDurationChange = (value: dayjs.Dayjs[]) => {
    form.setFieldValue(
      ExpFormField.START,
      value ? (value[0] ? value[0].toDate() : undefined) : undefined,
    );

    if (isBalancedShuffleTimeSlice) {
      form.setFieldValue(ExpFormField.END, undefined);
      form.setFieldValue(
        ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_DAY_LENGTH,
        undefined,
      );
    } else {
      form.setFieldValue(
        ExpFormField.END,
        value ? (value[1] ? value[1].toDate() : undefined) : undefined,
      );
    }

    // CUSTOM logic for duration validation
    form.setFieldValue('startEnd', value || undefined);
    const validateFields = (form as any).validateFields;
    if (typeof validateFields === 'function') {
      void validateFields(['startEnd']).catch(() => undefined);
    }
  };

  const getExperimentLengthOptions = () => {
    const treatmentCount = treatments.length;
    const options = [1, 2, 3].map((i) => {
      const numberOfDays = i * 7 * treatmentCount;
      return {
        label: `${numberOfDays} days`,
        value: numberOfDays,
      };
    });
    return options;
  };

  const handleExperimentLengthChange = (value: number) => {
    form.setFieldValue(
      ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_DAY_LENGTH,
      value,
    );
    const expectedFormattedEndTime = formattedStartTime
      .add(value, 'days')
      .subtract(1, 'minutes');
    const expectedEndTime = expectedFormattedEndTime.toDate();
    form.setFieldValue(ExpFormField.END, expectedEndTime || undefined);
    form.setFieldValue('startEnd', [
      formattedStartTime.toDate(),
      expectedEndTime,
    ]);
  };

  return (
    <StepWrapper title="Duration (Dates)">
      <DurationSection>
        {isBalancedShuffleTimeSlice && (
          <Space direction="vertical" style={{ rowGap: 0 }}>
            <Typography.Text strong>Start Date</Typography.Text>
            <Typography.Text type="secondary">
              Define the starting day and time of the experiment
            </Typography.Text>
          </Space>
        )}

        <Form.Item
          name="startEnd"
          rules={[
            {
              validator: async (_, value) => {
                if ([STARTED, RUNNING].includes(selectedExperiment?.state)) {
                  return Promise.resolve();
                }

                if (!value) {
                  return Promise.reject(new Error('Required'));
                }

                const startTime = value[0];
                const endTime = value[1];

                if (isBalancedShuffleTimeSlice) {
                  if (!startTime) {
                    return Promise.reject(new Error('Required'));
                  }
                } else {
                  if (!startTime || !endTime) {
                    return Promise.reject(new Error('Required'));
                  }
                }

                if (dayjs(endTime).isBefore(dayjs(startTime))) {
                  return Promise.reject(
                    new Error('End time must be after start time'),
                  );
                }

                if (dayjs(startTime).isBefore(dayjs())) {
                  return Promise.reject(
                    new Error('Start date has already passed'),
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
          style={{ marginBottom: 0, whiteSpace: 'pre-line' }}
          validateTrigger={['onBlur', 'onChange', 'onClick']}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <DurationSelectorWrapper>
              <DurationSelectorComponent
                value={[formattedStartTime, formattedEndTime]}
                restrictBy={restrictBy}
                domain={currentDomainName}
                geography={geography}
                isOverlap={overlapByDefault}
                isExpTimeSliceStrategy={[
                  ExperimentAssignmentStrategy.TIME_SLICE,
                  ExperimentAssignmentStrategy.TIME_SLICE_V2,
                  ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
                  ExperimentAssignmentStrategy.TIME_SLICE_GROUPED,
                  ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
                  ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE,
                  ExperimentAssignmentStrategy.EXPLICIT_RANGE,
                ].includes(strategyName)}
                onChange={onDurationChange}
                disabled={isViewMode}
                mode={isBalancedShuffleTimeSlice ? 'single' : 'range'}
              />
            </DurationSelectorWrapper>
            {!!durationInDays && !isBalancedShuffleTimeSlice && (
              <div style={{ paddingLeft: '8px' }}>
                <Typography.Text type="secondary">
                  Duration: {durationInDays} days
                </Typography.Text>
              </div>
            )}
          </div>
        </Form.Item>
        {isBalancedShuffleTimeSlice && (
          <>
            <Space direction="vertical" style={{ rowGap: 0 }}>
              <Typography.Text strong>
                Number of days to run the experiment
              </Typography.Text>
              <Typography.Text type="secondary">
                Define the number of days you desire to run this experiment.
                According to this, the end date will be generated.
              </Typography.Text>
            </Space>
            <Space>
              <Form.Item
                style={{
                  width: '450px',
                  margin: 0,
                }}
                name={
                  ExpFormField.STRATEGY.CUSTOM_TIME_SLICE_STRATEGY_DAY_LENGTH
                }
                rules={[
                  {
                    validator: async () => {
                      if (!durationInDays) {
                        return Promise.reject(new Error('Required'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  options={getExperimentLengthOptions()}
                  onChange={handleExperimentLengthChange}
                />
              </Form.Item>
              {formattedEndTime && (
                <Typography.Text type="secondary">
                  (End date: {formattedEndTime.format('YYYY-MM-DD HH:mm z')})
                </Typography.Text>
              )}
            </Space>
          </>
        )}
      </DurationSection>
    </StepWrapper>
  );
};

const DurationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DurationSelectorWrapper = styled.div`
  width: 450px;
`;

export default DurationStep;
